// §9.6 / §5.1 — launching an app.
//
// One place decides whether a launch opens a window or focuses an existing one, and where a new
// window lands. Placement comes from `wm/geometry` (§5.2) so the launcher, session restore and
// the Store all agree.
import type { AppManifest, InProcessWindowDefaults, Rect, VfsPath } from '@hangar/shared';
import type { Bus } from '../bus';
import { cascade } from '../wm/geometry';
import type { WmStore } from '../wm/store';
import { DEFAULT_WINDOW, sectionsOf, windowDefaults, type AppSection } from '@hangar/shared';
import type { AppMountService } from './inprocess';

export interface LaunchOptions {
  /** Force a second window even for a single-window app (used by `window.open`, §4.1). */
  forceNew?: boolean;
  title?: string;
  rect?: Rect;
  /**
   * §4.1 `window.open({width, height})` — this window's size, overriding the app's own defaults,
   * still **placed** by the cascade (D-540).
   *
   * Distinct from `rect`, which fixes the position too: a caller that knows how big a window should
   * be usually does not know where it belongs, and §34.4's pinned note is exactly that case.
   */
  size?: { w: number; h: number };
  /** §9.2 "Open with" — the file this window exists to open. Read back by `launch.openedPath`. */
  openPath?: VfsPath;
}

export interface LaunchableApp {
  id: string;
  name: string;
  keywords: readonly string[];
  category?: string;
  /** §29.1 (D-369) — the sub-group header this app sits under inside its category. */
  group?: string;
}

export interface AppLaunchService {
  /** Opens or focuses the app. Returns the window id, or `null` if the app is unknown. */
  launch(appId: string, options?: LaunchOptions): string | null;
  /** Every launchable app, for the launcher and the bar. */
  launchable(): LaunchableApp[];
  /**
   * §24.13 / §29.1 (D-225, D-369) — the same apps as headed sections.
   *
   * Here rather than in each skin so that three launchers and the Store cannot disagree about
   * which header a title lives under, or in what order the headers come.
   */
  grouped(): AppSection<LaunchableApp>[];
  /**
   * The path a window was launched to open (§9.2), or `null`. Kept here rather than on the `Win`
   * record: §5.1's record is persisted by session restore, and a grant is not (§9.5).
   */
  openedPath(instanceKey: string): VfsPath | null;
}

export interface AppLaunchDeps {
  bus: Bus;
  wm: WmStore;
  apps: AppMountService;
  /**
   * §9.6 — the installed external apps. A launch does not care which kind an app is: what differs
   * is only who fills the window afterwards (a mount, or an iframe), and that is the host's job.
   */
  installed: {
    get(appId: string): { manifest: AppManifest } | undefined;
    list(): Array<{ id: string; manifest: AppManifest }>;
  };
  /** The area a new window may occupy — the display minus the skin's chrome (§5.4). */
  workArea: () => Rect;
}

/** §9.2's `window` block collapsed onto §9.1's defaults, so both kinds place windows alike. */
function externalDefaults(manifest: AppManifest): InProcessWindowDefaults {
  const w = manifest.window ?? {};
  return {
    width: w.width ?? DEFAULT_WINDOW.width,
    height: w.height ?? DEFAULT_WINDOW.height,
    minWidth: w.minWidth ?? DEFAULT_WINDOW.minWidth,
    minHeight: w.minHeight ?? DEFAULT_WINDOW.minHeight,
    resizable: w.resizable ?? DEFAULT_WINDOW.resizable,
    multiWindow: w.multiWindow ?? DEFAULT_WINDOW.multiWindow,
  };
}

export function createAppLaunchService(deps: AppLaunchDeps): AppLaunchService {
  let opened = 0;
  const openPaths = new Map<string, VfsPath>();
  // A window's open-path dies with the window, exactly as its grant does (§9.5).
  deps.bus.on('window:closed', ({ instanceKey }) => openPaths.delete(instanceKey));

  return {
    openedPath: (instanceKey) => openPaths.get(instanceKey) ?? null,

    launch(appId, options = {}) {
      // A built-in wins a name collision: it cannot be uninstalled, so it is the one that has
      // to keep working.
      const app = deps.apps.get(appId);
      const external = deps.installed.get(appId);
      if (!app && !external) return null;

      const { defaults, name } = app
        ? { defaults: windowDefaults(app), name: app.name }
        : // `external` is defined here: the guard above returned when both were missing, and
          // this branch is the one where `app` was not found.
          {
            defaults: externalDefaults((external as { manifest: AppManifest }).manifest),
            name: (external as { manifest: AppManifest }).manifest.name,
          };

      if (!options.forceNew && !defaults.multiWindow) {
        const existing = deps.wm.list().find((w) => w.appId === appId);
        if (existing) {
          if (options.openPath) openPaths.set(existing.instanceKey, options.openPath);
          if (existing.state === 'minimized') deps.wm.restore(existing.id);
          deps.wm.focus(existing.id);
          return existing.id;
        }
      }

      const rect =
        options.rect ??
        cascade(
          opened++,
          options.size ?? { w: defaults.width, h: defaults.height },
          deps.workArea(),
        );

      const win = deps.wm.create({
        appId,
        rect,
        title: options.title ?? name,
        minW: defaults.minWidth,
        minH: defaults.minHeight,
        resizable: defaults.resizable,
      });

      if (options.openPath) openPaths.set(win.instanceKey, options.openPath);
      deps.bus.emit('app:launched', { appId, winId: win.id });
      return win.id;
    },

    launchable() {
      // §24.11 (D-200) — `visible()`, never `registered()`: a gated game must not appear in the
      // launcher or §12 search for a signed-out visitor.
      const builtIns = deps.apps
        .visible()
        .map((id) => deps.apps.get(id))
        .filter((app): app is NonNullable<typeof app> => app !== undefined)
        .map((app) => ({
          id: app.id,
          name: app.name,
          keywords: app.keywords ?? [],
          ...(app.category === undefined ? {} : { category: app.category }),
          ...(app.group === undefined ? {} : { group: app.group }),
        }));

      // §9.6 — an installed app is launchable exactly as a built-in is. A built-in wins a name
      // collision: it cannot be uninstalled, so it is the one that has to keep working.
      const known = new Set(builtIns.map((app) => app.id));
      const external = deps.installed
        .list()
        .filter((app) => !known.has(app.id))
        .map((app) => ({ id: app.id, name: app.manifest.name, keywords: [] as readonly string[] }));

      return [...builtIns, ...external].sort((a, b) => a.name.localeCompare(b.name));
    },

    grouped() {
      return sectionsOf(this.launchable());
    },
  };
}
